/**
 * Process one uploaded statement: download, parse and verify on the device,
 * classify, then hand everything to the database in a single import call.
 * Any failure is recorded on the upload with a factual reason.
 */
import type { Repo } from '../data/repo';
import { errorMessage } from '../data/repo';
import { isInvestmentAccount, type Account, type Category, type ImportResult, type ImportRow, type Upload } from '../data/types';
import type { ParseResult } from './parser/types';
import { classify } from './classify';
import { dedupeHashes } from './ledger/dedupe';
import type { PayeeRule } from './payee';

export type ProcessOutcome =
  | { ok: true; result: ImportResult; warnings: string[]; bank: string; currency: string }
  | { ok: false; error: string };

export interface ProcessDeps {
  parse: (file: File) => Promise<ParseResult>;
}

/** Match a parsed statement to a recorded account by bank name and currency. */
export function matchAccount(parsed: Pick<ParseResult, 'bank' | 'currency'>, accounts: Account[]): Account | null {
  const bank = parsed.bank.toLowerCase();
  const aliases = bank === 'gtbank' ? ['gtbank', 'gt bank', 'guaranty', 'gtco', 'gt '] : [bank];
  const sameCurrency = accounts.filter((a) => (a.currency || 'NGN').toUpperCase() === parsed.currency);
  const matches = sameCurrency.filter((a) => {
    const label = ` ${a.institution ?? ''} ${a.name} `.toLowerCase();
    return aliases.some((alias) => label.includes(alias));
  });
  // Only an unambiguous match is used. Never fall back to "any account in this currency".
  if (matches.length === 1) return matches[0]!;
  // A bank statement belongs to a transactional account, not an investment held at the same bank
  // (e.g. "GTBank NGN" vs "GT Money Market", both GTBank NGN).
  const transactional = matches.filter((a) => !isInvestmentAccount(a));
  return transactional.length === 1 ? transactional[0]! : null;
}

export async function processUpload(
  repo: Repo,
  upload: Upload,
  ctx: { monthStart: string; accounts: Account[]; categories: Category[]; payeeRules?: PayeeRule[] },
  deps: ProcessDeps,
): Promise<ProcessOutcome> {
  try {
    const blob = await repo.downloadStatement(upload);
    const file = new File([blob], upload.original_filename, { type: blob.type || 'application/pdf' });
    const parsed = await deps.parse(file);

    if (!parsed.reconciled) {
      const fmt = (n: number) => n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      const detail = parsed.rowIssues.slice(0, 6).map((r) =>
        `Row ${r.index + 1} (${r.date}, ${r.description.slice(0, 40)}): read ${r.parsedDirection} ${fmt(r.parsedAmount)}, balance moved ${r.balanceMovement < 0 ? '−' : '+'}${fmt(Math.abs(r.balanceMovement))}.`);
      const more = parsed.rowIssues.length > 6 ? ` …and ${parsed.rowIssues.length - 6} more.` : '';
      throw new Error(`${parsed.bank} ${parsed.currency} statement does not reconcile. ${parsed.blockers.join(' ')} ${detail.join(' ')}${more}`.trim());
    }
    const monthKey = ctx.monthStart.slice(0, 7);
    const outside = parsed.transactions.filter((t) => !t.date.startsWith(monthKey));
    if (outside.length) {
      throw new Error(`${outside.length} transaction(s) fall outside this month. Upload the statement for this month only.`);
    }

    // Create missing capital categories (Property, Business Capital, Internal Transfer) once, before classifying.
    let categories = ctx.categories;
    const rules = ctx.payeeRules ?? [];
    const first = parsed.transactions.map((t) => classify(t, parsed.currency, categories, rules));
    const missing = new Map(first.flatMap((c) => (c.missingSystemCategory ? [[c.missingSystemCategory.name, c.missingSystemCategory]] : [])));
    for (const m of missing.values()) {
      categories = [...categories, await repo.ensureCategory(m.name, m.type)];
    }

    const account = matchAccount(parsed, ctx.accounts);
    // Identity is the bank and currency, not the database account id, so it stays stable
    // even if account matching changes later.
    const hashes = await dedupeHashes({ accountKey: `${parsed.bank}:${parsed.currency}`, transactions: parsed.transactions });

    const rows: ImportRow[] = parsed.transactions.map((t, i) => {
      const c = classify(t, parsed.currency, categories, rules);
      return {
        tx_date: t.date,
        description: t.description,
        amount: t.amount,
        direction: t.direction,
        currency: parsed.currency,
        category_id: c.category?.id ?? null,
        classification: c.classification,
        confidence: c.confidence,
        is_internal_transfer: c.is_internal_transfer,
        is_reimbursement: c.is_reimbursement,
        is_business_funding: c.is_business_funding,
        needs_review: c.needs_review,
        source_hash: hashes[i]!,
      };
    });

    const result = await repo.importStatement({
      uploadId: upload.id,
      accountId: account?.id ?? null,
      summary: {
        opening: parsed.summary.opening,
        closing: parsed.summary.closing,
        debits: parsed.summary.debits,
        credits: parsed.summary.credits,
        bank: parsed.bank,
        currency: parsed.currency,
        opening_derived: parsed.summary.openingDerived,
        warnings: parsed.warnings,
        corrected_rows: parsed.transactions.filter((t) => t.amountSource !== 'parsed').map((t) => t.index),
      },
      rows,
    });
    const warnings = [...parsed.warnings];
    if (!account) warnings.push(`No single recorded account matches ${parsed.bank} ${parsed.currency}; transactions were imported without an account link.`);
    return { ok: true, result, warnings, bank: parsed.bank, currency: parsed.currency };
  } catch (e) {
    const error = errorMessage(e);
    await repo.markUploadFailed(upload.id, error).catch(() => undefined);
    return { ok: false, error };
  }
}
